import {useNavigate, useParams} from "react-router-dom";
import {Autocomplete, Divider, Stack, TextField, Typography} from "@mui/material";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import Property from "crqlar-react-common/dist/components/Property";
import LocalizedTextField
    from "crqlar-react-common/dist/components/LocalizedTextField";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {
    MappingCodeService
} from "crqlar-react-common/dist/services/mappings/mappingCodes.service";
import {
    MappingCode, MappingCodeType
} from "crqlar-react-common/dist/models/mappingCodes/mappingCode";
import {useMemo} from "react";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {isRequiredTextMissing} from "src/utils/requiredField.util";


const MappingCodeDetails = () => {
    useTrackScreenVisit("system mapping code details");

    const {t, i18n} = useTranslation();
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();

    const mappingData = useEntity<MappingCode>({
        get: async () => {
            if (id && id !== "new") {
                return await MappingCodeService.getMappingById(id)
            }
            return {code: ""};
        },
        create: MappingCodeService.add,
        update: MappingCodeService.update,
        delete: (id) => MappingCodeService.delete({id: [id]}),
        onDeleted: () => navigate('/system/mappingCodes', {replace: true}),
        onSaved: (mapping, wasCreateAction) => {
            if (wasCreateAction) {
                navigate(`/system/mappingCodes/${mapping.id}`, {replace: true});
            }
        },
        isValid: (mapping) => !!mapping?.code,
        cleanupBeforeSave: (mapping) => ({
            ...mapping,
            code: mapping.code?.trim()
        })
    }, id);

    const mapping = mappingData.item;
    const setMapping = mappingData.setItem;
    const disabled = false;
    const codeMissing = isRequiredTextMissing(mappingData, mapping.code);


    const updateField = <K extends keyof MappingCode>(field: K, value: MappingCode[K]) => {
        setMapping({...mapping, [field]: value});
    };


    const toTitleCase = (s: string) =>
        s.replace(/\w\S*/g, w => w[0].toUpperCase() + w.slice(1).toLowerCase());

    const defaultLabelForType = (value: MappingCodeType) => {
        if (value.startsWith("FOLIO_")) return `Folio ${toTitleCase(value.replace("FOLIO_", "").replace(/_/g, " "))}`;
        if (value.startsWith("OTA_")) return `OTA ${toTitleCase(value.replace("OTA_", "").replace(/_/g, " "))}`;
        if (value.startsWith("CRQLAR_")) return `CRQLAR ${toTitleCase(value.replace("CRQLAR_", "").replace(/_/g, " "))}`;
        return toTitleCase(value.replace(/_/g, " "));
    };

    const typeOptions = useMemo<{ value: MappingCodeType; label: string }[]>(
        () =>
            Object.values(MappingCodeType).map(value => ({
                value,
                label: t(`Common preference type ${value}`, { defaultValue: defaultLabelForType(value) })
            })),
        [t, i18n.language]
    );


    const titleSection = (
        <>
            <SettingsEntityHeader
                title={mapping.code}
                back={() => navigate('/system/mappingCodes')}
                data={mappingData}
            />
            <Stack mb={2} direction="row" justifyContent="flex-start" sx={{display: {xs: 'none', md: 'flex'}}}>
                <Typography variant="caption" color="text.secondary">
                    {t("Last change at", {time: mapping.updateDate?.toLocaleString?.() || "-"})}
                </Typography>
            </Stack>
        </>
    );

    const basicInfoSection = (
        <>
            <Typography variant="h6" textAlign="start">
                {t("Basic info")}
            </Typography>

            <Property
                title={t("Code")}
                tooltip={t('External mapping code received from or sent to connected systems.')}
            >
                <TextField
                    id="code"
                    size="small"
                    value={mapping.code || ""}
                    onChange={(e) => updateField("code", e.target.value)}
                    fullWidth
                    required
                    error={codeMissing}
                />
            </Property>

            <Divider/>

            <Property
                title={t("Description (internal only)")}
                tooltip={t('Staff-only note explaining what this mapping code represents.')}
            >
                <TextField
                    id="description"
                    size="small"
                    value={mapping.description || ""}
                    onChange={(e) => updateField("description", e.target.value)}
                    fullWidth
                    disabled={disabled}
                />
            </Property>

            <Divider/>

            <Property
                title={t("Type")}
                tooltip={t('Defines which kind of external value this code maps to.')}
            >
                <Autocomplete
                    id="type"
                    options={typeOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    value={typeOptions.find(o => o.value === mapping.type) ?? null}
                    onChange={(e, v) => updateField("type", v?.value ?? null)}
                    renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    fullWidth
                    disabled={disabled}
                />
            </Property>

        </>
    );

    const localizationsSection = (
        <>
            <Typography variant="h6" textAlign="start" sx={{mt: 6}}>
                {t("Translations")}
            </Typography>

            <Property
                title={t("Name translations")}
                tooltip={t('Localized display names for this mapped value.')}
            >
                <LocalizedTextField
                    id="localizations"
                    size="small"
                    value={mapping.localizations || {}}
                    onChange={(v) => updateField("localizations", v)}
                    fullWidth
                    disabled={disabled}
                />
            </Property>

        </>

    );

    return (
        <Stack sx={{
            width: "100%",
            maxWidth: "900px",
            gap: 1,
            alignSelf: "center"
        }}>
            {titleSection}
            {basicInfoSection}
            {localizationsSection}
        </Stack>
    );
};

export default MappingCodeDetails;
